<template>
  <v-card variant="flat" tile>
    <v-card-text class="pa-8">
      <!-- Editable description for Draft revisions -->
      <div v-if="revision.isDraft()">
        <h3>Edit Instruction Description</h3>
        <div>
          <b>Note:</b> Editing the description will be reflected in your published instruction.
        </div>
        <v-textarea
          class="mt-4"
          v-model="localDescription"
          placeholder="Enter instruction description..."
          variant="outlined"
          rows="3"
          auto-grow
          :loading="isPending"
          :disabled="isPending"
          @input="handleDescriptionChange"
          hide-details
        />
      </div>
      <!-- Read-only description for non-Draft revisions -->
      <div v-else>
        <h3>Instruction Description</h3>
        <div class="mt-4" style="white-space: pre-line">{{ instruction.form.description }}</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue'
import { debounce } from 'lodash'
import type { ISystemError } from '@/models/error'
import type { IPostOrderInstructionRevision, IPostOrderInstruction } from '@/models/post-order'
import { useUpdatePostOrderInstruction } from '@/composables/post-order'
import { PostOrderSymbol } from './postOrderProvide'

interface Props {
  instruction: IPostOrderInstruction
  revision: IPostOrderInstructionRevision
}

const props = defineProps<Props>()

// TODO MG 2025-05-11: move set instruction state feature to this component to remove unnecessary error model
const error = defineModel<ISystemError | null>('error', {
  default: null
})

// Get post order context
const postOrderContext = inject(PostOrderSymbol)
if (!postOrderContext) {
  throw new Error('[Post Order] Could not find injected post order context')
}

// Local description state
const localDescription = ref(props.instruction.form.description || '')

// Watch for changes in instruction description to update local state
watch(
  () => props.instruction.form.description,
  (newDescription) => {
    localDescription.value = newDescription || ''
  },
  { immediate: true }
)

// Update instruction mutation
const {
  isPending,
  error: updateError,
  mutate: updateInstruction,
  reset: clearUpdateError
} = useUpdatePostOrderInstruction(computed(() => postOrderContext.postOrderId.value))

// Watch for update errors
watch(updateError, (newError) => {
  if (newError) {
    error.value = newError
  }
})

// Save description function
function saveDescription() {
  if (localDescription.value === props.instruction.form.description) {
    return // No changes to save
  }

  clearUpdateError()

  const updatedInstruction = {
    ...props.instruction,
    form: {
      ...props.instruction.form,
      description: localDescription.value
    }
  }

  updateInstruction(updatedInstruction, {
    onSuccess: () => {
      error.value = null
    },
    onError: (updateError) => {
      // Revert local description on error
      localDescription.value = props.instruction.form.description || ''
      error.value = updateError
    }
  })
}

// Debounced save function (saves after user stops typing for 1 second)
const debouncedSave = debounce(saveDescription, 1000)

// Handle description input changes
function handleDescriptionChange() {
  debouncedSave()
}
</script>
